import {
  BillingCurrency,
  TransactionCurrency,
} from '@contracts/payment/Currency';
import type { ExchangeRate } from '@contracts/payment/ExchangeRate';
import type { PaymentMethod } from '@contracts/payment/PaymentMethod';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import styled from '@mui/material/styles/styled';
import TableContainer from '@mui/material/TableContainer';
import { Banner } from '@src/common/components/Banner';
import { Stack } from '@src/common/components/Stack';
import { Table, TableBody, TableHead } from '@src/common/components/Table';
import { Typography } from '@src/common/components/Typography';
import { theme } from '@src/common/styles/Theme';
import type { Billing } from '@src/orders/domain/Billing';
import { isCAD, isUSD } from '@src/orders/domain/Billing';
import type { ReviewableOrder } from '@src/orders/domain/ReviewableOrder';
import type { Transaction } from '@src/orders/domain/Transaction';
import { CreditCardTotalRows } from '@src/orders/review/CreditCardTotalRows';
import { CurrencyCell } from '@src/orders/review/CurrencyCell';
import { EditAdminFeeRateButton } from '@src/orders/review/EditAdminFeeRateButton';
import { InvoiceCurrencyDropdown } from '@src/orders/review/InvoiceCurrencyDropdown';
import { LineOfCreditTotalRow } from '@src/orders/review/LineOfCreditTotalRow';
import {
  BorderedTableCell,
  HeaderBorderedTableCell,
  SubHeaderBorderedTableCell,
  WideRow,
} from '@src/orders/review/StyledTableRows';
import { useTranslation } from 'react-i18next';

export interface BillingSectionProps {
  data?: DefaultProps;
  onCurrencyChange: ({
    billingCurrency,
  }: {
    billingCurrency: BillingCurrency;
  }) => void;
  order: ReviewableOrder;
  transactions: Transaction[];
  readonly?: boolean;
}

export type DefaultProps = {
  currency?: BillingCurrency;
  paymentMethod: PaymentMethod;
  totals: Billing;
};

export type CreditCardProps = {
  currency: BillingCurrency;
  paymentMethod: 'CREDIT_CARD';
  totals: Billing;
};

export const BillingSection = ({
  data,
  onCurrencyChange,
  order,
  transactions,
  readonly,
}: BillingSectionProps) => {
  const { t } = useTranslation();
  const showSurchargeMessage = data?.totals.hasSurcharge();

  return (
    <Stack paddingBottom={5}>
      <StyledStack
        direction="row"
        justifyContent="space-between"
        marginBottom={2}
        alignItems="center"
      >
        <Typography variant="subtitle1" style={{ padding: theme.spacing['3'] }}>
          {t('reviewBilling.billing')}
        </Typography>
        {order.isInvoicing() && (
          <InvoiceCurrencyDropdown
            defaultCurrency={data?.currency}
            disabled={!order.canChangeBillingCurrency(transactions)}
            paymentMethod={data?.paymentMethod ?? order.billTo.paymentMethod}
            onCurrencyChange={onCurrencyChange}
          />
        )}
      </StyledStack>

      {data?.currency && (
        <>
          <TotalTable
            data={data}
            exchangeRate={order.exchangeRate}
            orderId={order.id}
            adminFeeRate={order.adminFeeRate}
            canEditAdminFeeRate={order.canEditAdminFeeRate && !readonly}
          />
          {showSurchargeMessage && (
            <Banner
              severity="info"
              style={{
                width: '100%',
                padding: theme.spacing['2'],
                marginTop: theme.spacing['4'],
              }}
            >
              <InfoOutlinedIcon />
              <Stack flex={1}>
                <Typography>{t('reviewBilling.surchargeMessage')}</Typography>
              </Stack>
            </Banner>
          )}
        </>
      )}
    </Stack>
  );
};

const StyledStack = styled(Stack)(() => ({
  '& :last-child': {
    flex: 'unset',
  },
}));

export const TotalTable = ({
  data,
  exchangeRate,
  orderId,
  adminFeeRate,
  canEditAdminFeeRate,
}: {
  data: DefaultProps | CreditCardProps;
  exchangeRate: ExchangeRate;
  adminFeeRate: number;
  canEditAdminFeeRate: boolean;
  orderId: string;
}) => {
  const { t } = useTranslation();
  const { CAD: CADColumn, USD: USDColumn } = data.totals.billingTable;
  const currency = data.currency ?? BillingCurrency.CAD;

  return (
    <TableContainer aria-labelledby="billing-table">
      <Table size="small" aria-label={t('reviewBilling.billingTotals')}>
        <TableHead>
          <WideRow
            sx={{
              backgroundColor: theme.palette.primary.onPrimary,
            }}
          >
            <HeaderBorderedTableCell />
            <HeaderBorderedTableCell
              variant="head"
              align="right"
              sx={{
                color: isCAD(currency)
                  ? 'inherit'
                  : theme.palette.secondary.secondaryFixedDim,
              }}
            >
              {TransactionCurrency.CAD}
            </HeaderBorderedTableCell>
            <HeaderBorderedTableCell
              variant="head"
              align="right"
              sx={{
                color: isUSD(currency)
                  ? 'inherit'
                  : theme.palette.secondary.secondaryFixedDim,
              }}
            >
              {TransactionCurrency.USD}
            </HeaderBorderedTableCell>
          </WideRow>
          <WideRow>
            <SubHeaderBorderedTableCell variant="footer" align="right">
              {t('reviewBilling.exchangeRate')}
            </SubHeaderBorderedTableCell>
            <SubHeaderBorderedTableCell variant="footer" align="right">
              {t('reviewBilling.usdToCad', { rate: exchangeRate.USDToCAD })}
            </SubHeaderBorderedTableCell>
            <SubHeaderBorderedTableCell variant="footer" align="right">
              {t('reviewBilling.cadToUsd', { rate: exchangeRate.CADToUSD })}
            </SubHeaderBorderedTableCell>
          </WideRow>
        </TableHead>
        <TableBody>
          <WideRow>
            <BorderedTableCell variant="head" align="right">
              {t('reviewPermit.stateFees')}
            </BorderedTableCell>
            <CurrencyCell
              cellCurrency={TransactionCurrency.CAD}
              currency={currency}
              value={data.totals.stateFees.CAD}
            />
            <CurrencyCell
              cellCurrency={TransactionCurrency.USD}
              currency={currency}
              value={data.totals.stateFees.USD}
            />
          </WideRow>
          <WideRow>
            <BorderedTableCell variant="head" align="right">
              {t('reviewPermit.serviceFees')}
            </BorderedTableCell>
            <CurrencyCell
              cellCurrency={TransactionCurrency.CAD}
              currency={currency}
              value={data.totals.serviceFees.CAD}
            />
            <CurrencyCell
              cellCurrency={TransactionCurrency.USD}
              currency={currency}
              value={data.totals.serviceFees.USD}
            />
          </WideRow>
          <WideRow>
            <BorderedTableCell variant="head" align="right">
              {t('reviewBilling.otherFees')}
            </BorderedTableCell>
            <CurrencyCell
              cellCurrency={TransactionCurrency.CAD}
              currency={currency}
              value={CADColumn.otherFee}
            />
            <CurrencyCell
              cellCurrency={TransactionCurrency.USD}
              currency={currency}
              value={USDColumn.otherFee}
            />
          </WideRow>

          <WideRow>
            <BorderedTableCell variant="head" align="right">
              {t('reviewBilling.adminFees')}
              {canEditAdminFeeRate && (
                <EditAdminFeeRateButton
                  orderId={orderId}
                  initialAdminFeeRate={adminFeeRate}
                />
              )}
            </BorderedTableCell>
            <CurrencyCell
              cellCurrency={TransactionCurrency.CAD}
              currency={currency}
              value={data.totals.adminFees.CAD}
            />
            <CurrencyCell
              cellCurrency={TransactionCurrency.USD}
              currency={currency}
              value={data.totals.adminFees.USD}
            />
          </WideRow>
          <WideRow aria-labelledby="tax-cell">
            <BorderedTableCell id="tax-cell" variant="head" align="right">
              {t('reviewBilling.tax')}
            </BorderedTableCell>
            <CurrencyCell
              cellCurrency={TransactionCurrency.CAD}
              currency={currency}
              value={data.totals.taxes.CAD}
            />
            <CurrencyCell
              cellCurrency={TransactionCurrency.USD}
              currency={currency}
              value={data.totals.taxes.USD}
            />
          </WideRow>

          {data.paymentMethod === 'LINE_OF_CREDIT' && (
            <LineOfCreditTotalRow
              currency={currency}
              totals={data.totals.getTotalFees()}
            />
          )}
          {data.paymentMethod === 'CREDIT_CARD' && (
            <CreditCardTotalRows currency={currency} totals={data.totals} />
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
